import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EditableFieldBase } from '../editable-field-base';
import { RtiUrlModalComponent } from './url-modal/rti-url-modal.component';

@Component({
    selector: 'rich-text-input',
    templateUrl: './rich-text-input.component.html',
    styleUrls: ['./rich-text-input.component.scss', '../editable-fields.scss'],
    standalone: false
})
export class RichTextInput extends EditableFieldBase implements OnInit, OnChanges {
  @Input() maxLength: number;                     // the maximum character length of the input field
  @Input() placeholder: string = '';              // what to show when the value is empty
  @Input() defaultHeight: number = 0;

  public lastSelectedText: any;
  public selectedTextColor: string = '#000000';
  public buttonGroups: any[] = [
    [
      new RichTextButton('Add Link', 'createLink', 'fa-solid fa-link-horizontal'),
      new RichTextButton('Remove Link', 'unlink', 'fa-solid fa-link-horizontal-slash'),
    ],
    [
      new RichTextButton('Bold', 'bold', 'fa-solid fa-bold'),
      new RichTextButton('Italic', 'italic', 'fa-solid fa-italic'),
      new RichTextButton('Text Color', 'forecolor', 'fa-solid fa-text'),
      new RichTextButton('Remove Formatting', 'removeFormat', 'fa-solid fa-text-slash'),
    ],
    [
      new RichTextButton('Ordered List', 'insertOrderedList', 'fa-light fa-list-ol'),
      new RichTextButton('Unordered List', 'insertUnorderedList', 'fa-light fa-list-ul'),
      new RichTextButton('Indent', 'indent', 'fa-light fa-indent'),
      new RichTextButton('Outdent', 'outdent', 'fa-light fa-outdent'),
    ],
  ];
  public colorOptions: any[] = [
    ['#000000', '#6c757d', '#dc3545', '#fd7e14'],
    ['#ffc107', '#198754', '#0d6efd', '#6f42c1']
  ]

  @ViewChild('richTextInput') input!: ElementRef<HTMLDivElement>;

  private _isViewInitialized: boolean;

  constructor(private modalService: NgbModal) {
    super();
  }

  public ngOnInit() {
    super.ngOnInit();    
  }

  public ngAfterViewInit(): void {
    this._isViewInitialized = true;
    this.input.nativeElement.innerHTML = this.obj[this.prop] || '';
  }

  // Make sure the html loads the latest course description  
  public ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (!!changes['obj']) {
      if (this._isViewInitialized) // PP - added on new line, when combined with the if on the previous line was causing errors in chrome redux extension
        this.input.nativeElement.innerHTML = this.obj[this.prop] || '';
    }
    
  }

  protected onChange() {
    //  This appropriately empties the html when the user deletes all text.  Otherwise a
    //  rogue <br/> hangs around.
    if (!this.input.nativeElement.innerText?.trim()?.length)
      this.input.nativeElement.innerHTML = '';

    this.obj[this.prop] = this.input.nativeElement.innerHTML;
    this.validate(); 
    super.onChange(); 
  }

  //  If any text was selected when we left the input, retain that selection just in case
  //  they user pushed a formatting button.
  public onBlur() {
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);

    this.lastSelectedText = range ?? undefined;
  }

  //  This can be triggered when the color is chosen if there is already text highlighted.
  public selectColor(color: string) {
    this.selectedTextColor = color;

    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(this.lastSelectedText);

    this.editText('forecolor');
  }

  //  Method for applying the formatting to the text in the div.
  public editText(action: string) {
    // First check for highlighted text
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0)
      return;

    let highlightedText = '';
    const range = selection.getRangeAt(0);

    // Check if selection is within the div, ignore if it's outside
    if (this.input.nativeElement.contains(range.commonAncestorContainer))
      highlightedText = selection.toString();

    //  Only trigger commands if the text inside the input is highlighted
    //  BIG IMPORTANT WARNING ABOUT EXECCOMMAND - This has been deprecated basically since its inception
    //  but at this point it STILL doesn't have an implemented replacement and it's the only way to do
    //  a WYSIWYG rich text editor.  Input Events Level 2 is still working draft https://www.w3.org/TR/input-events-2/
    //  and can't reliably be used yet so it seems like the general consensus is just use execCommand
    //  and to avoid getting your rich text editor TOO fancy. - June 2025 CS
    if (!!highlightedText) {
      if (action == 'createLink') {
        var modalRef = this.modalService.open(RtiUrlModalComponent, { backdrop: 'static', size: 'md' });

        modalRef.result
          .then(
            (url: string) => {
              //  This prevents the user from accidentally making a relative link.  If the user doesn't include http or https the
              //  url could end up constructed as something like https://mykeadmin.kenan-flagler.unc.edu/admin/test/google.com.
              if (!url.startsWith('http'))
                url = `https://${url}`;

              //  We need to get the stored last selection because opening the modal cleared it out.
              const selection = window.getSelection();
              selection?.removeAllRanges();
              selection?.addRange(this.lastSelectedText);

              if (!selection || selection.rangeCount === 0)
                return;

              const range = selection.getRangeAt(0);
              const selectedText = range.extractContents();

              const anchor = document.createElement('a');
              anchor.href = url;
              anchor.target = '_blank';
              anchor.appendChild(selectedText);
              range.insertNode(anchor);
            },
            (dismissed) => { }
          );
      }
      else if (action == 'indent') {
        //  We only want to indent if this is in an ol or ul.  Otherwise this makes a blockquote that
        //  doesn't function as expected in the div.
        if (!!this.isList(range.commonAncestorContainer))
          document.execCommand(action, false, selection.toString());
      }
      else if (action == 'forecolor')
        document.execCommand(action, false, this.selectedTextColor);
      else
        document.execCommand(action, false, selection.toString());
    }

    this.onChange();
  }

  //  Checks through the node to confirm we're in a list.
  private isList(node) {
    const acceptableNodes = ['OL', 'UL', 'LI'];

    while (node && node.nodeType !== 1)
      node = node.parentNode;

    return acceptableNodes.includes(node?.tagName);
}

  //  To keep things safer and for consistent behavior, we're doing the paste action
  //  without using the execCommand and we're stripping out anything other than plain text.
  public onPaste(event: ClipboardEvent) {
    event.preventDefault();
    let clipboardData = event.clipboardData
    let pastedText = clipboardData.getData('text/plain');

    //  Make sure the selection was in the input, otherwise we're not pasting anything.
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0)
      return;

    const range = selection.getRangeAt(0);
    range.deleteContents();

    const newNode = document.createTextNode(pastedText);
    range.insertNode(newNode);

    this.onChange();
  }

}

export class RichTextButton {
  public label: string;
  public action: string;
  public icon: string;

  constructor(label: string, action: string, icon: string) {
    this.label = label;
    this.action = action;
    this.icon = icon;
  }
}
